import { useState, useRef, type ChangeEvent } from 'react';
import { formatBytes } from '@pirouette/shared';
import { useUploadMedia, type UploadProgress } from '@/data/media';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

const ACCEPT_VIDEO = 'video/mp4,video/quicktime,video/webm';
const ACCEPT_PHOTO = 'image/jpeg,image/png,image/webp';
const MAX_SIZE = 500_000_000;

interface MediaUploaderProps {
  /** Either dance_id OR competition_routine_id must be set. */
  danceId?: string;
  competitionRoutineId?: string;
  /** Defaults to 'video'. Photos use a tighter type allowlist. */
  defaultKind?: 'video' | 'photo';
  onComplete?: (assetId: string) => void;
}

export function MediaUploader({
  danceId,
  competitionRoutineId,
  defaultKind = 'video',
  onComplete,
}: MediaUploaderProps) {
  const upload = useUploadMedia();
  const [kind, setKind] = useState<'video' | 'photo'>(defaultKind);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    if (f && f.size > MAX_SIZE) {
      toast({
        title: 'File too large',
        description: `${formatBytes(f.size)} exceeds the 500 MB cap.`,
        variant: 'error',
      });
      e.target.value = '';
      return;
    }
    setFile(f);
    setProgress(null);
  }

  async function submit() {
    if (!file) return;
    if (!danceId && !competitionRoutineId) {
      toast({
        title: 'Internal error',
        description: 'MediaUploader needs danceId or competitionRoutineId.',
        variant: 'error',
      });
      return;
    }
    try {
      const result = await upload.mutateAsync({
        file,
        init: {
          kind,
          dance_id: danceId,
          competition_routine_id: competitionRoutineId,
          content_type: file.type,
          size_bytes: file.size,
          title: title.trim() || undefined,
          description: description.trim() || undefined,
        },
        onProgress: setProgress,
      });
      toast({ title: 'Upload complete', variant: 'success' });
      setFile(null);
      setTitle('');
      setDescription('');
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
      onComplete?.(result.asset_id);
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <section aria-labelledby="uploader-heading" className="surface-elevated grid gap-4 p-5">
      <h3 id="uploader-heading" className="font-[var(--font-display)] text-[var(--text-lg)]">
        Upload {kind === 'video' ? 'video' : 'photo'}
      </h3>

      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-[var(--text-sm)]">
          <input type="radio" checked={kind === 'video'} onChange={() => setKind('video')} />
          Video
        </label>
        <label className="flex items-center gap-2 text-[var(--text-sm)]">
          <input type="radio" checked={kind === 'photo'} onChange={() => setKind('photo')} />
          Photo
        </label>
      </div>

      <div>
        <Label htmlFor="m-file">File</Label>
        <input
          id="m-file"
          ref={inputRef}
          type="file"
          accept={kind === 'video' ? ACCEPT_VIDEO : ACCEPT_PHOTO}
          onChange={onFile}
          className="block w-full text-[var(--text-sm)]"
        />
        {file && (
          <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
            {file.name} · {formatBytes(file.size)}
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="m-title">Title</Label>
        <Input
          id="m-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Final stage run-through"
        />
      </div>

      <div>
        <Label htmlFor="m-desc">Description</Label>
        <Input id="m-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>

      {progress && (
        <div className="space-y-1">
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress.percent}
            className="h-2 w-full overflow-hidden rounded-[var(--radius-full)] bg-[var(--color-surface-sunken)]"
          >
            <div
              className="h-full bg-[var(--color-accent)] transition-[width] duration-[var(--duration-fast)]"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-xs)] tabular-nums">
            {progress.percent}% · {formatBytes(progress.loaded)} of {formatBytes(progress.total)}
          </p>
        </div>
      )}

      <Button onClick={() => void submit()} disabled={!file || upload.isPending}>
        {upload.isPending
          ? progress && progress.percent < 100
            ? 'Uploading…'
            : 'Finalizing…'
          : 'Upload'}
      </Button>
    </section>
  );
}
